import axios from 'axios';
import { getToken, clearSession, isDemoTab } from './session';

// Runtime URL — always resolves to the current deployment domain
// (prevents stale build-time env vars from misdirecting API calls)
const BACKEND_URL = window.location.origin;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
    baseURL: API_BASE,
    headers: {
        'Content-Type': 'application/json',
    },
    // v3.5.13 — Hard-cap every request at 30s so the UI never hangs forever on
    // a stalled backend (e.g. production running a stale build with a blocked
    // event loop). Uploads still override with their own longer timeouts.
    timeout: 30000,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
    const token = getToken();
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    // v4.33.0 — Multi-organization context. Sidebar org switcher persists
    // the active org under `yulontap_active_org`. Special value
    // `__all__` means "aggregate across every assigned organization".
    // Single-org accounts never write this key → header is omitted → the
    // backend falls through to its legacy single-org filter. Zero
    // behavior change for legacy users.
    // A demo tab never inherits the real admin's org selection.
    const activeOrg = isDemoTab() ? null : localStorage.getItem('yulontap_active_org');
    if (activeOrg) {
        config.headers['X-Active-Org-Id'] = activeOrg;
    }
    return config;
});

// Normalize FastAPI validation errors so UI code can always treat `detail` as a string.
// Pydantic 422 errors come as: detail: [{type, loc, msg, input, ctx}, ...]
// Without this, any component that renders `{error.detail}` in JSX or passes it to
// toast.error() would crash with "Objects are not valid as a React child".
function normalizeErrorDetail(data) {
    if (!data || typeof data !== 'object') return data;
    const d = data.detail;
    if (Array.isArray(d)) {
        const parts = d.map((item) => {
            if (!item || typeof item !== 'object') return String(item);
            const loc = Array.isArray(item.loc) ? item.loc.filter((p) => p !== 'body').join('.') : '';
            const msg = item.msg || item.message || 'Invalid value';
            return loc ? `${loc}: ${msg}` : msg;
        });
        data.detail = parts.join('; ');
    } else if (d && typeof d === 'object') {
        data.detail = d.msg || d.message || JSON.stringify(d);
    }
    return data;
}

// Handle auth errors
// An EXPIRED medical-demo token must never land a presenter on a login screen
// or on Operational Base: the persona is re-minted silently and the request is
// retried once. Scoped to the medical demo personas (the key is only ever set
// by /demo/medical/*), so no other session behaviour changes.
const MED_PERSONA_KEY = 'yulontap_med_demo_persona';

const remintMedicalDemo = async (config) => {
    let persona = null;
    try { persona = sessionStorage.getItem(MED_PERSONA_KEY); } catch (e) { return null; }
    if (!persona || config?._medRetried) return null;
    const res = await fetch(`${BACKEND_URL}/api/demo-med/${persona}/session`);
    if (!res.ok) return null;
    const d = await res.json().catch(() => ({}));
    if (!d.access_token) return null;
    const { setDemoSession } = await import('./session');
    setDemoSession(d.access_token, d.user || {});
    return api({ ...config, _medRetried: true,
        headers: { ...(config.headers || {}), Authorization: `Bearer ${d.access_token}` } });
};

api.interceptors.response.use(
    (response) => response,
    async (error) => {
        if (error.response?.data) {
            normalizeErrorDetail(error.response.data);
        }
        if (error.response?.status === 401) {
            const retried = await remintMedicalDemo(error.config).catch(() => null);
            if (retried) return retried;
            // Don't redirect if on a public page or this is a logout call
            const path = window.location.pathname;
            const isPublicPage = path === '/login'
                || path === '/change-password'
                || path.startsWith('/landing/')
                || path.startsWith('/review-shield/')
                || path.startsWith('/review-shield-experience/')
                || path.startsWith('/global-review/')
                || path.startsWith('/hotel/')
                || path.startsWith('/bridge/')
                || path.startsWith('/respond-now/')
                || path.startsWith('/staff/')
                || path.startsWith('/manager/')
                || path.startsWith('/voucher/')
                || path.startsWith('/vehicle/')
                || path.startsWith('/tap-entry/')
                || path === '/redeem'
                || path === '/forgot-password'
                || path === '/reset-password';
            const isLogoutCall = error.config?.url?.includes('/auth/logout');
            if (!isPublicPage && !isLogoutCall) {
                clearSession();
                window.location.href = '/login';
            }
        }
        return Promise.reject(error);
    }
);

// Auth API
export const authAPI = {
    login: (email, password) => api.post('/auth/login', { email, password }),
    register: (data) => api.post('/auth/register', data),
    getMe: () => api.get('/auth/me'),
    logout: () => api.post('/auth/logout'),
    changePassword: (currentPassword, newPassword) => 
        api.post('/auth/change-password', { current_password: currentPassword, new_password: newPassword }),
    // P_MORNING_BRIEF — persists the user's chosen language on the
    // server. After a 200, the AuthProvider calls i18n.changeLanguage.
    updateLanguage: (preferredLanguage) =>
        api.patch('/auth/me/language', { preferred_language: preferredLanguage }),
    // P_THEME_PERSISTENCE_1.0 — persists the user's light/dark choice
    // on the server. Mirrors the language contract so the preference
    // survives iPad Safari ITP, new browsers, and new devices.
    updateTheme: (preferredTheme) =>
        api.patch('/auth/me/theme', { preferred_theme: preferredTheme }),
    // Demo Setting (super admin only) — 60-min idle window + multiple windows.
    updateDemoSession: (enabled) =>
        api.patch('/auth/me/demo-session', { enabled }),
    updateSidebarPrefs: (hiddenTabs) =>
        api.patch('/auth/me/sidebar-prefs', { hidden_tabs: hiddenTabs }),
};

// Brands API
export const brandsAPI = {
    list: () => api.get('/brands'),
    get: (id) => api.get(`/brands/${id}`),
    create: (data) => api.post('/brands', data),
    update: (id, data) => api.put(`/brands/${id}`, data),
    delete: (id) => api.delete(`/brands/${id}`),
};

// Broadcasts API (Phase 2 — Mission Control Broadcast Center)
export const broadcastsAPI = {
    // Admin (super_admin only)
    listAdmin: (status) => api.get('/admin/broadcasts', { params: status ? { status } : {} }),
    getAdmin: (id) => api.get(`/admin/broadcasts/${id}`),
    create: (data) => api.post('/admin/broadcasts', data),
    update: (id, data) => api.patch(`/admin/broadcasts/${id}`, data),
    delete: (id) => api.delete(`/admin/broadcasts/${id}`),
    publish: (id) => api.post(`/admin/broadcasts/${id}/publish`),
    unpublish: (id) => api.post(`/admin/broadcasts/${id}/unpublish`),
    // Reader
    listActive: (surface) => api.get('/broadcasts/active', { params: surface ? { surface } : {} }),
    dismiss: (id) => api.post(`/broadcasts/${id}/dismiss`),
};

// Timeline API (Phase 3 — v4.40 unified Recent Activity feed)
export const timelineAPI = {
    list: (filter = 'all', limit = 200) => api.get('/timeline/events', { params: { filter, limit } }),
};

// SMS demo-notification switch (super admin)
export const smsAPI = {
    status: () => api.get('/sms/status'),
    setSuppression: (organizationId, mode) => api.put('/sms/suppression', { organization_id: organizationId, mode }),
};

// Organizations API
export const organizationsAPI = {
    list: (brandId) => api.get('/organizations', { params: brandId ? { brand_id: brandId } : {} }),
    get: (id) => api.get(`/organizations/${id}`),
    create: (data) => api.post('/organizations', data),
    update: (id, data) => api.put(`/organizations/${id}`, data),
    delete: (id) => api.delete(`/organizations/${id}`),
    permanentDelete: (id) => api.delete(`/organizations/${id}/permanent`),
    updateUrls: (id, data) => api.put(`/organizations/${id}/urls`, data),
    updatePlatform: (id, platform) => api.put(`/organizations/${id}/platform`, { platform }),
    updateAllCardsPlatform: (id, platform) => api.put(`/organizations/${id}/all-cards-platform`, { platform }),
    togglePlatformLock: (id) => api.put(`/organizations/${id}/toggle-platform-lock`),
    toggleSurveysModule: (id, enabled) => api.put(`/organizations/${id}/surveys-module`, { enabled }),
    toggleManageSurveysPermission: (id, enabled) => api.put(`/organizations/${id}/manage-surveys-permission`, { enabled }),
    updateEnabledLanguages: (id, enabled_languages) => api.put(`/organizations/${id}/enabled-languages`, { enabled_languages }),
    // P_ORG_TIMEZONE_AWARE_SCHEDULING_1.0 — IANA timezone + per-channel
    // hour fields. Payload accepts any subset of:
    //   { timezone, welcome_hour_local, voucher_hour_local,
    //     weekly_report_hour_local, recognition_hour_local }
    updateSchedulingProfile: (id, payload) =>
        api.put(`/organizations/${id}/scheduling-profile`, payload),
    setVertical: (orgId, vertical) =>
        api.put(`/organizations/${orgId}/vertical`, { vertical }),
};

// Locations API
export const locationsAPI = {
    list: () => api.get('/locations'),
    get: (id) => api.get(`/locations/${id}`),
    create: (data) => api.post('/locations', data),
    update: (id, data) => api.put(`/locations/${id}`, data),
    delete: (id) => api.delete(`/locations/${id}`),
    // v4.31.0 — Guest Routing & Review Platforms (per-location).
    getPlatformUrls: (id) => api.get(`/locations/${id}/platform-urls`),
    updatePlatformUrls: (id, platform_urls) =>
        api.put(`/locations/${id}/platform-urls`, { platform_urls }),
    // P_PHASE_4_8_REPUTATION_BASELINE_FALLBACK — manual current snapshot
    // (super_admin only at the backend level; baseline stays immutable).
    updateCurrentSnapshot: (id, snapshot) =>
        api.patch(`/locations/${id}/current-snapshot`, snapshot),
    // Auto-clear open recovery alerts after N days (0 = Off). Archives, never deletes.
    setAutoClearRecovery: (id, days) =>
        api.put(`/locations/${id}/auto-clear-recovery`, { days }),
};

// Employees API
export const hotelRoomsAPI = {
    list: (organizationId) => api.get('/hotel-rooms', { params: organizationId ? { organization_id: organizationId } : {} }),
    create: (data) => api.post('/hotel-rooms', data),
    createRange: (data) => api.post('/hotel-rooms/range', data),
    update: (id, data) => api.put(`/hotel-rooms/${id}`, data),
    delete: (id) => api.delete(`/hotel-rooms/${id}`),
    assignCard: (roomId, cardId, action = 'assign') => api.put(`/hotel-rooms/${roomId}/cards`, { card_id: cardId, action }),
    getFreeEntry: (organizationId) => api.get(`/assistant/admin/free-room-entry/${organizationId}`),
    setFreeEntry: (organizationId, allow) => api.post('/assistant/admin/free-room-entry', { organization_id: organizationId, allow }),
};

// ── Property Tap Entry · Person Layer (Phase 2) ──────────────────────────
// RESIDENT self-service: authenticated by the resident PIN session token
// (X-Resident-Token header), NOT the staff Bearer token. Uses a bare axios
// call so no staff auth/org headers leak onto these public resident endpoints.
const _resHdr = (token) => ({ headers: { 'X-Resident-Token': token } });
export const residentPeopleAPI = {
    me: (token) => axios.get(`${API_BASE}/property/resident/me`, _resHdr(token)),
    listPeople: (token) => axios.get(`${API_BASE}/property/resident/people`, _resHdr(token)),
    addPerson: (token, body) => axios.post(`${API_BASE}/property/resident/people`, body, _resHdr(token)),
    listActions: (token) => axios.get(`${API_BASE}/property/resident/actions`, _resHdr(token)),
    addAction: (token, body) => axios.post(`${API_BASE}/property/resident/actions`, body, _resHdr(token)),
    patchAction: (token, actionId, body) => axios.patch(`${API_BASE}/property/resident/actions/${actionId}`, body, _resHdr(token)),
    deleteAction: (token, actionId) => axios.delete(`${API_BASE}/property/resident/actions/${actionId}`, _resHdr(token)),
    setSmsPrefs: (token, body) => axios.put(`${API_BASE}/property/resident/sms-prefs`, body, _resHdr(token)),
};

// ── Shared Notification Routing Layer (SMS) — PLATFORM, vertical-agnostic ────
// Same endpoints for every vertical: event toggles, record→person routing and
// each person's own permanent on/off + temporary pause.
export const notifyAPI = {
    me: () => api.get('/notify/me'),
    updateMe: (body) => api.put('/notify/me', body),
    eventSettings: (organizationId, vertical) => api.get('/notify/event-settings', { params: { organization_id: organizationId, vertical } }),
    setEventSettings: (organizationId, events, vertical) => api.put('/notify/event-settings', { organization_id: organizationId, events, vertical }),
    people: (organizationId) => api.get('/notify/people', { params: { organization_id: organizationId } }),
    routing: (kind, organizationId) => api.get(`/notify/routing/${kind}`, { params: { organization_id: organizationId } }),
    setRouting: (kind, body) => api.put(`/notify/routing/${kind}`, body),
};

// ── Action History (SHARED, vertical-agnostic) ───────────────────────────────
// Same endpoints for every surface; only the credential header differs.
const _ahHdr = (opts = {}) => {
    if (opts.staffToken) return { headers: { 'X-Staff-Session': opts.staffToken } };
    if (opts.residentToken) return { headers: { 'X-Resident-Token': opts.residentToken } };
    return {};
};
export const actionHistoryAPI = {
    list: (opts, params) => (opts?.staffToken || opts?.residentToken
        ? axios.get(`${API_BASE}/action-history/me`, { ..._ahHdr(opts), params })
        : api.get('/action-history/me', { params })).then((r) => r.data),
    dismiss: (opts, eventIds) => (opts?.staffToken || opts?.residentToken
        ? axios.post(`${API_BASE}/action-history/dismiss`, { event_ids: eventIds }, _ahHdr(opts))
        : api.post('/action-history/dismiss', { event_ids: eventIds })).then((r) => r.data),
    clear: (opts) => (opts?.staffToken || opts?.residentToken
        ? axios.post(`${API_BASE}/action-history/clear`, {}, _ahHdr(opts))
        : api.post('/action-history/clear', {})).then((r) => r.data),
    timeline: (kind, recordId, organizationId, opts) => (opts?.staffToken || opts?.residentToken
        ? axios.get(`${API_BASE}/action-history/timeline/${kind}/${recordId}`, { ..._ahHdr(opts), params: { organization_id: organizationId } })
        : api.get(`/action-history/timeline/${kind}/${recordId}`, { params: { organization_id: organizationId } })).then((r) => r.data),
};
// ADMIN (staff MANAGERS) person + action management — standard authenticated
// `api` instance (Bearer). The server enforces the MANAGERS role + org scope;
// the UI never re-implements that as security.
export const propertyPeopleAdminAPI = {
    listPeople: (unitId) => api.get(`/property/units/${unitId}/people`),
    addPerson: (unitId, body) => api.post(`/property/units/${unitId}/people`, body),
    updatePerson: (personId, body) => api.patch(`/property/people/${personId}`, body),
    deactivatePerson: (personId) => api.patch(`/property/people/${personId}/deactivate`),
    unitTapEntry: (unitId) => api.get(`/property/units/${unitId}/tap-entry`),
    saveUnitTapEntry: (unitId, body) => api.put(`/property/units/${unitId}/tap-entry`, body),
    listActions: (personId) => api.get(`/property/people/${personId}/actions`),
    addAction: (personId, body) => api.post(`/property/people/${personId}/actions`, body),
    patchAction: (actionId, body) => api.patch(`/property/actions/${actionId}`, body),
    deleteAction: (actionId) => api.delete(`/property/actions/${actionId}`),
};

export const hotelOpsAPI = {
    floors: (organizationId) => api.get('/hotel-ops/floors', { params: { organization_id: organizationId } }),
    floorDetail: (organizationId, floor) => api.get('/hotel-ops/floor-detail', { params: { organization_id: organizationId, floor } }),
    roomHistory: (organizationId, room_number) => api.get('/hotel-ops/room-history', { params: { organization_id: organizationId, room_number } }),
    activity: (organizationId, limit = 100) => api.get('/hotel-ops/activity', { params: { organization_id: organizationId, limit } }),
    photoBlob: (organizationId, photoId) => api.get(`/hotel-ops/photo/${photoId}`, { params: { organization_id: organizationId }, responseType: 'blob' }),
};

export const employeesAPI = {
    list: (locationId) => api.get('/employees', { params: { location_id: locationId } }),
    get: (id) => api.get(`/employees/${id}`),
    create: (data) => api.post('/employees', data),
    update: (id, data) => api.put(`/employees/${id}`, data),
    delete: (id) => api.delete(`/employees/${id}`),
    setPin: (employeeId, pin) => api.post(`/vouchers/admin/employee-pins/${employeeId}/set`, { pin }),
    removePin: (employeeId) => api.delete(`/vouchers/admin/employee-pins/${employeeId}`),
};

// Cards API
export const vouchersAPI = {
    // Voucher audit trail (voucher_audit_events). `event` is a comma-separated
    // filter, e.g. "voucher_downloaded,voucher_download_blocked".
    giftAudit: (organizationId, params = {}) =>
        api.get(`/vouchers/gift/audit/${organizationId}`, { params }),
};

export const vehiclesAPI = {
    list: (organizationId, status) => api.get('/vehicles', { params: { ...(organizationId ? { organization_id: organizationId } : {}), ...(status ? { status } : {}) } }),
    get: (id) => api.get(`/vehicles/${id}`),
    create: (data) => api.post('/vehicles', data),
    update: (id, data) => api.put(`/vehicles/${id}`, data),
    remove: (id) => api.delete(`/vehicles/${id}`),
    availableCards: (organizationId, vehicleId) => api.get('/vehicles/available-cards', { params: { organization_id: organizationId, ...(vehicleId ? { vehicle_id: vehicleId } : {}) } }),
    assignCard: (id, cardId) => api.post(`/vehicles/${id}/assign-card`, { card_id: cardId }),
    unassignCard: (id) => api.post(`/vehicles/${id}/unassign-card`),
    markSold: (id) => api.post(`/vehicles/${id}/mark-sold`),
    markActive: (id) => api.post(`/vehicles/${id}/mark-active`),
    vinLookup: (vin, organizationId, refresh = false) => api.post('/vehicles/vin-lookup', { vin, organization_id: organizationId, refresh }),
    featureOptions: () => api.get('/vehicles/feature-options'),
};

export const vehicleActivityAPI = {
    summary: (organizationId, days = 7) => api.get('/vehicle-activity/summary', { params: { organization_id: organizationId, days } }),
    byVehicle: (organizationId, days = 30) => api.get('/vehicle-activity/by-vehicle', { params: { organization_id: organizationId, days } }),
    inventory: (organizationId, days = 30, locationIds = null) => api.get('/vehicle-activity/inventory', { params: { organization_id: organizationId, days, ...(locationIds && locationIds.length ? { location_ids: locationIds.join(',') } : {}) } }),
    stream: (organizationId, params = {}) => api.get('/vehicle-activity/stream', { params: { organization_id: organizationId, ...params } }),
    vehicleDetail: (vehicleId) => api.get(`/vehicle-activity/vehicle/${vehicleId}`),
    conversation: (conversationId) => api.get(`/vehicle-activity/conversation/${conversationId}`),
};

export const vehicleLeadsAPI = {
    testDrives: (organizationId, status) => api.get('/test-drives', { params: { organization_id: organizationId, ...(status ? { status } : {}) } }),
    confirmTestDrive: (id) => api.post(`/test-drives/${id}/confirm`),
    declineTestDrive: (id) => api.post(`/test-drives/${id}/decline`),
    salesRequests: (organizationId) => api.get('/vehicle-sales/requests', { params: { organization_id: organizationId } }),
    thread: (reqId) => api.get(`/vehicle-sales/requests/${reqId}/messages`),
    reply: (reqId, data) => api.post(`/vehicle-sales/requests/${reqId}/reply`, data),
    decision: (reqId, data) => api.post(`/vehicle-sales/requests/${reqId}/decision`, data),
};

// F&I queue — financing applications + warranty interest, with an internal
// `lead_status` that never touches the credit provider's own `status`.
export const fiLeadsAPI = {
    financing: (organizationId) => api.get('/vehicle-financing-applications', { params: { organization_id: organizationId } }),
    warranty: (organizationId) => api.get('/vehicle-warranty-interest', { params: { organization_id: organizationId } }),
    setStatus: (kind, leadId, status) => api.patch(`/${kind}/${leadId}/status`, { status }),
};

// Showroom Settings — dealer logo + e-transfer email (manager/admin only).
export const vehicleSettingsAPI = {
    get: (organizationId) => api.get('/vehicle-settings', { params: { organization_id: organizationId } }),
    update: (organizationId, data) => api.put('/vehicle-settings', data, { params: { organization_id: organizationId } }),
};

// Per-vehicle broadcast list (who gets texted about ONE vehicle) + bulk mute.
export const vehicleBroadcastAPI = {
    get: (vehicleId) => api.get(`/vehicles/${vehicleId}/broadcast`),
    update: (vehicleId, data) => api.put(`/vehicles/${vehicleId}/broadcast`, data),
    bulkMute: (data) => api.post('/vehicles/broadcast/mute', data),
};

export const autoAlertsAPI = {
    list: (organizationId, scope = 'command_center') => api.get('/automotive/alerts', { params: { organization_id: organizationId, scope } }),
    markRead: (organizationId, scope, alertKeys = null, all = false) => api.post('/automotive/alerts/read', { organization_id: organizationId, scope, alert_keys: alertKeys, all }),
    dismiss: (organizationId, scope, alertKey) => api.post('/automotive/alerts/dismiss', { organization_id: organizationId, scope, alert_key: alertKey }),
};

// Alert-delivery health — "is anyone actually receiving these?" (empty recipient
// lists are silent by design, so this is what makes them visible).
export const tradeInsAPI = {
    list: (organizationId) => api.get('/vehicle-trade-ins', { params: { organization_id: organizationId } }),
    setAppraisal: (tradeInId, appraisedValue, notes) =>
        api.put(`/vehicle-trade-ins/${tradeInId}/appraisal`, { appraised_value: appraisedValue, notes: notes ?? null }),
};

export const alertHealthAPI = {
    get: (organizationId) => api.get('/alert-health', { params: { organization_id: organizationId } }),
    summary: (onlyDeaf = true) => api.get('/alert-health/summary', { params: { only_deaf: onlyDeaf } }),
};

export const buyerActivityAPI = {
    visits: (conversationId) => api.get('/buyer-activity/visits', { params: { conversation_id: conversationId } }),
    timeline: (conversationId, sessionId, since = null) => api.get('/buyer-activity', {
        params: { conversation_id: conversationId, session_id: sessionId, ...(since ? { since } : {}) },
    }),
    // Which buyers are on the page right now — one call for a whole board.
    live: (organizationId) => api.get('/buyer-activity/live', { params: { organization_id: organizationId } }),
    // The OTHER vehicles this buyer looked at on the same walk around the lot.
    sameVisit: (conversationId, sessionId) => api.get('/buyer-activity/same-visit', {
        params: { conversation_id: conversationId, session_id: sessionId },
    }),
    // STANDALONE ENTRY POINT — one row per buyer SESSION, no conversation
    // needed, so a buyer who has only tapped a card is reachable too.
    liveBuyers: (organizationId, hours = 24) => api.get('/buyer-activity/live-buyers', {
        params: { organization_id: organizationId, hours },
    }),
    sessionTimeline: (organizationId, sessionId, since = null) => api.get('/buyer-activity/session', {
        params: { organization_id: organizationId, session_id: sessionId, ...(since ? { since } : {}) },
    }),
    sessionSameVisit: (organizationId, sessionId) => api.get('/buyer-activity/session/same-visit', {
        params: { organization_id: organizationId, session_id: sessionId },
    }),
};

export const salesDeskAPI = {
    managerChat: (organizationId) => api.get('/sales-desk/manager-chat', { params: { organization_id: organizationId } }),
    managerChatUnread: (organizationId) => api.get('/sales-desk/manager-chat/unread', { params: { organization_id: organizationId } }),
    sendManagerChat: (organizationId, body) => api.post('/sales-desk/manager-chat', { organization_id: organizationId, body }),
    resetDemo: () => api.post('/demo-auto/reset', {}),
};

export const rebateAlertsAPI = {
    list: (organizationId, locationIds = null) => api.get('/automotive/rebate-alerts', { params: { organization_id: organizationId, ...(locationIds && locationIds.length ? { location_ids: locationIds.join(',') } : {}) } }),
    authorize: (instanceId, incentive = null, reason = null) => api.post(`/automotive/special-instances/${instanceId}/authorize`, { incentive, reason }),
    decline: (instanceId, reason = null) => api.post(`/automotive/special-instances/${instanceId}/decline`, { reason }),
    revoke: (instanceId, reason = null) => api.post(`/automotive/special-instances/${instanceId}/revoke`, { reason }),
    vehicleStatus: (vehicleId, organizationId) => api.get(`/automotive/vehicle-status/${vehicleId}`, { params: { organization_id: organizationId } }),
    // Aging-incentive schedules — nothing can ever become eligible without one.
    schedules: (organizationId) => api.get('/automotive/special-schedules', { params: { organization_id: organizationId } }),
    createSchedule: (body) => api.post('/automotive/special-schedules', body),
    updateSchedule: (id, body) => api.put(`/automotive/special-schedules/${id}`, body),
    deleteSchedule: (id) => api.delete(`/automotive/special-schedules/${id}`),
    evaluate: (organizationId) => api.post('/automotive/special-schedules/evaluate', null, { params: { organization_id: organizationId } }),
};

export const cardsAPI = {
    list: (params) => api.get('/cards', { params }),
    get: (id) => api.get(`/cards/${id}`),
    create: (data) => api.post('/cards', data),
    createBulk: (data) => api.post('/cards/bulk', data),
    assign: (id, employeeId) => api.put(`/cards/${id}/assign`, { employee_id: employeeId }),
    updatePlatform: (id, platform) => api.put(`/cards/${id}/platform`, { platform }),
    bulkUpdatePlatform: (cardIds, platform) => api.put('/cards/bulk/platform', { card_ids: cardIds, platform }),
    updateOrganization: (id, organizationId, locationId) => 
        api.put(`/cards/${id}/organization`, { organization_id: organizationId, location_id: locationId }),
    bulkUpdateOrganization: (cardIds, organizationId, locationId) => 
        api.put('/cards/bulk/organization', { card_ids: cardIds, organization_id: organizationId, location_id: locationId }),
    updateStatus: (id, status) => api.put(`/cards/${id}/status`, null, { params: { status } }),
    updateName: (id, name) => api.put(`/cards/${id}/name`, { name }),
    updateLocation: (id, locationId) => api.put(`/cards/${id}/location`, { location_id: locationId }),
    updateLandingSettings: (id, settings) => api.put(`/cards/${id}/landing-settings`, settings),
    // Single-field language override. Deliberately separate from
    // updateLandingSettings, which replaces the whole settings object.
    updateEnabledLanguages: (id, enabled_languages) => api.put(`/cards/${id}/enabled-languages`, { enabled_languages }),
    delete: (id) => api.delete(`/cards/${id}`),
};

// Landing Page API (public - no auth required)
export const vehiclePublicAPI = {
    get: (cardId, sessionId, visitId = null) => axios.get(`${API_BASE}/vehicle/${cardId}`,
        (sessionId || visitId)
            ? { params: { ...(sessionId ? { session_id: sessionId } : {}),
                          ...(visitId ? { visit_id: visitId } : {}) } }
            : undefined),
    // Side-effect-free promotion slice — safe to poll (the full GET records a
    // tap and fires the scan alert, so it must only run on a real visit).
    promo: (cardId) => axios.get(`${API_BASE}/vehicle/${cardId}/promo`),
    // "Still here" heartbeat, piggybacked on the promo poll tick above so it
    // adds no new timer. Presence only — writes no activity event.
    presence: (cardId, sessionId, visitId = null) => axios.post(
        `${API_BASE}/vehicle/${cardId}/presence`,
        { session_id: sessionId, visit_id: visitId }),
    // Buyer-reported activity (CARFAX opened, payment settled). Fire-and-forget.
    activity: (cardId, kind, sessionId, metadata) => axios.post(
        `${API_BASE}/vehicle/${cardId}/fi-activity`,
        { kind, session_id: sessionId, metadata: metadata || {} }),
    // sessionId is what makes a special-offer click show up in THIS buyer's
    // timeline; an event without it cannot be attributed to a visit.
    offerEvent: (cardId, kind, sessionId = null) => axios.post(
        `${API_BASE}/vehicle/${cardId}/offer-event`, null,
        { params: { kind, ...(sessionId ? { session_id: sessionId } : {}) } }),
    contactSales: (cardId, data) => axios.post(`${API_BASE}/vehicle/${cardId}/contact-sales`, data),
    testDrive: (cardId, data) => axios.post(`${API_BASE}/vehicle/${cardId}/test-drive`, data),
    save: (cardId, data) => axios.post(`${API_BASE}/vehicle/${cardId}/save`, data),
    uploadPhoto: (cardId, file) => {
        const fd = new FormData();
        fd.append('file', file);
        return axios.post(`${API_BASE}/vehicle/${cardId}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    },
    compare: (cardIds) => axios.post(`${API_BASE}/vehicle/compare`, { card_ids: cardIds }),
    compareEmail: (data) => axios.post(`${API_BASE}/vehicle/compare/email`, data),
    getConversation: (reqId) => axios.get(`${API_BASE}/vehicle/conversation/${reqId}`),
    postConversation: (reqId, body) => axios.post(`${API_BASE}/vehicle/conversation/${reqId}/message`, { body }),
    offerDeposit: (cardId, data) => axios.post(`${API_BASE}/vehicle/${cardId}/offer-deposit`, data),
    counterResponse: (reqId, decision) => axios.post(`${API_BASE}/vehicle/conversation/${reqId}/counter-response`, { decision }),
    share: (cardId, data) => axios.post(`${API_BASE}/vehicle/${cardId}/share`, data),
    openShare: (shareToken) => axios.post(`${API_BASE}/vehicle/share/${shareToken}/open`),
};

// Property Tap Entry — public visitor endpoints (no auth). Slice 5.
export const tapEntryAPI = {
    directory: (locationId) => axios.get(`${API_BASE}/property/tap-entry/${locationId}/directory`),
    resolve: (locationId, unitNumber, entryCode) =>
        axios.post(`${API_BASE}/property/tap-entry/${locationId}/resolve`, { unit_number: unitNumber, entry_code: entryCode }),
    session: (token) => axios.get(`${API_BASE}/property/tap-entry/session/${token}`),
    personActions: (token, personId) =>
        axios.get(`${API_BASE}/property/tap-entry/session/${token}/person/${personId}/actions`),
    leave: (token) => axios.post(`${API_BASE}/property/tap-entry/session/${token}/leave`),
    cameras: (token) => axios.get(`${API_BASE}/property/tap-entry/session/${token}/cameras`),
    messages: (token, seen = false) => axios.get(`${API_BASE}/property/tap-entry/session/${token}/messages`, { params: { seen } }),
    sendMessage: (token, text, personId = null) => axios.post(`${API_BASE}/property/tap-entry/session/${token}/messages`, { text, person_id: personId }),
    openAction: (token, actionId) => axios.post(`${API_BASE}/property/tap-entry/session/${token}/actions/${actionId}/open`, {}),
};

// ── Visitor ↔ resident conversation (Access) ─────────────────────────────
// RESIDENT side of the SAME thread the visitor screen reads. Resident session
// token (X-Resident-Token); the link variant needs no login and opens ONE
// visitor conversation only.
export const residentVisitorsAPI = {
    list: (token) => axios.get(`${API_BASE}/property/resident/visitors`, _resHdr(token)),
    thread: (token, sessionId) => axios.get(`${API_BASE}/property/resident/visitors/${sessionId}/messages`, _resHdr(token)),
    reply: (token, sessionId, body) => axios.post(`${API_BASE}/property/resident/visitors/${sessionId}/reply`, { body }, _resHdr(token)),
    notify: (token, sessionId) => axios.post(`${API_BASE}/property/resident/visitors/${sessionId}/notify`, {}, _resHdr(token)),
};
export const visitorThreadLinkAPI = {
    thread: (linkToken) => axios.get(`${API_BASE}/property/visitor-thread/${linkToken}`),
    reply: (linkToken, body) => axios.post(`${API_BASE}/property/visitor-thread/${linkToken}/reply`, { body }),
    notify: (linkToken) => axios.post(`${API_BASE}/property/visitor-thread/${linkToken}/notify`, {}),
};


export const landingAPI = {
    getData: (cardId) => axios.get(`${API_BASE}/landing/${cardId}`),
    recordTap: (cardId, dwellSeconds = 0) => axios.post(`${API_BASE}/landing/${cardId}/tap`, { dwell_seconds: dwellSeconds }),
    reportPlaybackEvent: (videoId, event, data = {}) =>
        axios.post(`${API_BASE}/media/video/${videoId}/playback-event`, { event, ...data }),
};

// Upload API
export const uploadAPI = {
    uploadLogo: (file) => {
        const formData = new FormData();
        formData.append('file', file);
        return api.post('/upload/logo', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },
    uploadImage: (file) => {
        const formData = new FormData();
        formData.append('file', file);
        return api.post('/upload/logo', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },
    getUploadUrl: (filename) => `${API_BASE}/uploads/${filename}`,
    uploadVideo: async (file, onProgress) => {
        const CHUNK_SIZE = 450 * 1024; // 450KB chunks (under proxy limits)
        const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

        // Step 1: Initialize upload session
        const initRes = await api.post('/upload/video-init', {
            filename: file.name,
            file_size: file.size,
            content_type: file.type || 'video/mp4',
            total_chunks: totalChunks
        });
        const { upload_id } = initRes.data;

        // Step 2: Upload chunks
        for (let i = 0; i < totalChunks; i++) {
            const start = i * CHUNK_SIZE;
            const end = Math.min(start + CHUNK_SIZE, file.size);
            const chunk = file.slice(start, end);

            const chunkForm = new FormData();
            chunkForm.append('file', chunk, `chunk_${i}`);
            chunkForm.append('upload_id', upload_id);
            chunkForm.append('chunk_number', String(i));

            await api.post('/upload/video-chunk', chunkForm, {
                headers: { 'Content-Type': 'multipart/form-data' },
                timeout: 60000
            });

            if (onProgress) {
                onProgress({ loaded: end, total: file.size });
            }
        }

        // Step 3: Signal processing phase to UI
        if (onProgress) {
            onProgress({ loaded: file.size, total: file.size, processing: true });
        }

        // Step 4: Complete — starts background processing, returns immediately
        const completeRes = await api.post('/upload/video-complete', {
            upload_id: upload_id
        });

        const { video_id } = completeRes.data;

        // Step 5: Poll for processing completion
        if (completeRes.data.status === 'processing') {
            const maxPolls = 120; // 4 minutes max
            for (let i = 0; i < maxPolls; i++) {
                await new Promise(r => setTimeout(r, 2000));
                try {
                    const statusRes = await api.get(`/upload/video-status/${video_id}`);
                    if (statusRes.data.status === 'complete') {
                        return { data: statusRes.data };
                    }
                } catch (err) {
                    if (err.response?.status === 500) {
                        throw new Error(err.response?.data?.detail || 'Video processing failed');
                    }
                }
            }
            throw new Error('Video processing timed out. Please try again.');
        }

        return completeRes;
    },
    deleteVideo: (videoId) => api.delete(`/media/video/${videoId}`),
    getVideoUrl: (videoId) => `${API_BASE}/media/video/${videoId}`,
    getPosterUrl: (videoId) => `${API_BASE}/media/poster/${videoId}`,
};

// Taps API
export const tapsAPI = {
    list: (params) => api.get('/taps', { params }),
};

// Dashboard API
export const dashboardAPI = {
    getStats: () => api.get('/dashboard/stats'),
};

// Admin API
export const adminAPI = {
    setOrganizationPlatform: (orgId, platform) => 
        api.put(`/admin/organization/${orgId}/platform`, null, { params: { platform } }),
    togglePlatformLock: (orgId, locked) =>
        api.put(`/admin/organization/${orgId}/platform-lock`, null, { params: { locked } }),
    updatePlatformUrls: (orgId, platformUrls) =>
        api.put(`/organizations/${orgId}/platform-urls`, platformUrls),
    updateAllCardsPlatform: (orgId, platform) =>
        api.put(`/organizations/${orgId}/all-cards-platform`, { platform }),
    listUsers: (organizationId) => api.get('/admin/users', { params: { organization_id: organizationId } }),
    deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
    clearDemoData: () => api.post('/admin/clear-demo-data'),
    repairTapEmployees: () => api.post('/admin/repair-tap-employees'),
    toggleUrlRegistryAccess: (orgId, enabled) =>
        api.put(`/organizations/${orgId}/url-registry-access`, null, { params: { enabled } }),
};

// URL Registry API
export const urlRegistryAPI = {
    list: (organizationId) => api.get('/url-registry', { params: organizationId ? { organization_id: organizationId } : {} }),
};

// Platform Values API
// Sales/Calculation Tool — review calculator + branded one-pager.
export const salesCalculationAPI = {
    overview: (params) => api.get('/sales-calculation-tool/overview', { params }),
    locations: (organizationId) => api.get('/sales-calculation-tool/locations', { params: { organization_id: organizationId } }),
    freestyle: (data) => api.post('/sales-calculation-tool/freestyle', data),
    sendOnePager: (data) => api.post('/sales-calculation-tool/send-onepager', data),
    onePagerPdf: (data) => api.post('/reports/review-calculations/pdf', data, { responseType: 'blob' }),
    onePagerImages: (data) => api.post('/reports/review-calculations/pdf', data, { params: { images: true } }),
};

export const metricProfilesAPI = {
    list: () => api.get('/metric-profiles'),
    create: (data) => api.post('/metric-profiles', data),
    update: (id, data) => api.put(`/metric-profiles/${id}`, data),
    duplicate: (id) => api.post(`/metric-profiles/${id}/duplicate`),
    remove: (id) => api.delete(`/metric-profiles/${id}`),
};

export const platformValuesAPI = {
    getDefaults: () => api.get('/platform-values/defaults'),
    updateDefaults: (values) => api.put('/platform-values/defaults', values),
    getOrgValues: (orgId) => api.get(`/platform-values/organization/${orgId}`),
    updateOrgValues: (orgId, values) => api.put(`/platform-values/organization/${orgId}`, values),
    resetOrgValues: (orgId) => api.delete(`/platform-values/organization/${orgId}`),
    getCurrency: (orgId) => api.get(`/platform-values/organization/${orgId}/currency`),
    updateCurrency: (orgId, settings) => api.put(`/platform-values/organization/${orgId}/currency`, settings),
};

// ROI API
export const roiAPI = {
    getMonthly: (year, month, organizationId) => 
        api.get('/roi/monthly', { params: { year, month, organization_id: organizationId } }),
    getHistorical: (months, organizationId) => 
        api.get('/roi/historical', { params: { months, organization_id: organizationId } }),
    getOrgSummary: (orgId) => api.get(`/roi/organization/${orgId}/summary`),
};

// Reports API
export const reportsAPI = {
    getSalesReport: (startDate, endDate, organizationId, organizationIds) => 
        api.get('/reports/sales', { params: { start_date: startDate, end_date: endDate,
            organization_id: organizationId,
            organization_ids: (organizationIds || []).join(',') || undefined } }),
    getComparisonReport: (organizationIds, startDate, endDate) => 
        api.get('/reports/comparison', { params: { organization_ids: organizationIds.join(','), start_date: startDate, end_date: endDate } }),
};

// Scheduled Report Delivery Engine API
export const reportSchedulesAPI = {
    list: (reportKey) => api.get('/report-schedules', { params: { report_key: reportKey } }),
    create: (data) => api.post('/report-schedules', data),
    update: (id, data) => api.put(`/report-schedules/${id}`, data),
    remove: (id) => api.delete(`/report-schedules/${id}`),
    toggle: (id) => api.post(`/report-schedules/${id}/pause`),
    runNow: (id) => api.post(`/report-schedules/${id}/run-now`),
    runs: (id) => api.get(`/report-schedules/${id}/runs`),
    preview: (data) => api.post('/report-schedules/preview', data, { responseType: 'blob' }),
    previewImages: (data) => api.post('/report-schedules/preview', data, { params: { images: true } }),
    schedulePreviewImages: (id) => api.post(`/report-schedules/${id}/preview-images`),
    recipientInfo: (email) => api.get('/report-schedules/recipient-info', { params: { email } }),
    portalUsers: (orgIds) => api.get('/report-schedules/portal-users', { params: { organization_ids: (orgIds || []).join(',') } }),
    getBranding: (organizationId) => api.get('/report-branding', { params: { organization_id: organizationId || undefined } }),
    putBranding: (organizationId, branding) => api.put('/report-branding', { organization_id: organizationId || null, branding }),
    downloadSalesPdf: (data) => api.post('/reports/sales/pdf', data, { responseType: 'blob' }),
    salesPdfImages: (data) => api.post('/reports/sales/pdf', data, { params: { images: true } }),
};

// Email Notifications API
export const emailAPI = {
    getSettings: (employeeId) => api.get(`/email-settings/${employeeId}`),
    updateSettings: (employeeId, settings) => api.put(`/email-settings/${employeeId}`, settings),
    sendTest: (employeeId) => api.post(`/email/send-test/${employeeId}`),
    preview: (employeeId) => api.post(`/email/preview/${employeeId}`),
    getLogs: (params) => api.get('/email/logs', { params }),
};

// Employee Earnings & Leaderboard API
export const earningsAPI = {
    getEmployeeEarnings: (employeeId) => api.get(`/employees/${employeeId}/earnings`),
    getLeaderboard: (params) => api.get('/leaderboard', { params }),
};

// Shift Insights API
export const shiftInsightsAPI = {
    getSummary: (params) => api.get('/shift-insights/summary', { params }),
    getLeaderboard: (params) => api.get('/shift-insights/leaderboard', { params }),
    getShiftTimes: (orgId) => api.get(`/organizations/${orgId}/shift-times`),
    updateShiftTimes: (orgId, shiftTimes) => api.put(`/organizations/${orgId}/shift-times`, shiftTimes),
};

// Location Health API
export const locationHealthAPI = {
    getAll: (params) => api.get('/location-health', { params }),
    getDetail: (locId) => api.get(`/location-health/${locId}`),
    getBaseline: (locId) => api.get(`/locations/${locId}/baseline`),
    updateBaseline: (locId, baseline) => api.put(`/locations/${locId}/baseline`, baseline),
    bulkUpdateBaseline: (entries) => api.put('/locations/bulk/baseline', { entries }),
    bulkSetBaselinesByName: (data) => api.post('/locations/bulk/baseline-by-name', data),
    getLocationsWithoutBaseline: (organizationId) => api.get('/locations/without-baseline', { params: { organization_id: organizationId } }),
    getHealthThresholds: (orgId) => api.get(`/organizations/${orgId}/health-thresholds`),
    updateHealthThresholds: (orgId, thresholds) => api.put(`/organizations/${orgId}/health-thresholds`, thresholds),
    getIncrementalROI: (params) => api.get('/incremental-roi', { params }),
};

// Executive Summary API
export const executiveSummaryAPI = {
    getSummary: (params) => api.get('/executive-summary', { params }),
};

// Multi-Brand Comparison API
export const brandAPI = {
    getBrands: () => api.get('/brands'),
    getComparison: (params) => api.get('/brand-comparison', { params }),
    updateOrgBrand: (orgId, brand) => api.put(`/organizations/${orgId}/brand`, null, { params: { brand } }),
};

// Users API
export const usersAPI = {
    list: (organizationId) => api.get('/admin/users', { params: { organization_id: organizationId } }),
    get: (id) => api.get(`/admin/users/${id}`),
    create: (data) => api.post('/admin/users', data),
    update: (id, data) => api.put(`/admin/users/${id}`, data),
    delete: (id) => api.delete(`/admin/users/${id}`),
    permanentDelete: (id) => api.delete(`/admin/users/${id}/permanent`),
    resetPassword: (id, newPassword) => api.post(`/admin/users/${id}/reset-password`, { new_password: newPassword }),
    toggleStatus: (id) => api.post(`/admin/users/${id}/toggle-status`),
    getActivity: (id, limit = 50) => api.get(`/admin/users/${id}/activity`, { params: { limit } }),
    getAllActivity: (limit = 100, actionType = null) => api.get('/admin/activity-log', { params: { limit, action_type: actionType } }),
    sendCredentials: (id, password) => api.post(`/admin/users/${id}/send-credentials`, { password }),
};

// Audit Logs API (Super Admin only)
export const auditAPI = {
    getLogs: (params) => api.get('/admin/audit-logs', { params }),
    getActions: () => [
        { value: 'login_success', label: 'Login Success' },
        { value: 'login_failed', label: 'Login Failed' },
        { value: 'logout', label: 'Logout' },
        { value: 'session_expired', label: 'Session Expired' },
        { value: 'session_kicked', label: 'Session Kicked' },
        { value: 'password_change', label: 'Password Change' },
        { value: 'user_create', label: 'User Created' },
        { value: 'user_update', label: 'User Updated' },
        { value: 'user_delete', label: 'User Deleted' },
        { value: 'role_change', label: 'Role Changed' },
        { value: 'card_create', label: 'Card Created' },
        { value: 'card_update', label: 'Card Updated' },
        { value: 'card_delete', label: 'Card Deleted' },
        { value: 'export_full', label: 'Full Export' },
        { value: 'export_summary', label: 'Summary Export' },
        { value: 'export_denied', label: 'Export Denied' },
        { value: 'permission_denied', label: 'Permission Denied' },
    ],
    getActiveSessions: () => api.get('/admin/active-sessions'),
    exportCsv: (params) => api.get('/admin/audit-logs/export', { params, responseType: 'blob' }),
};

// v4.34.10 — Redemption Investigation API
// Compliance-grade investigation surface backed by the immutable
// employee-attribution snapshots emitted via redemption_audit.py.
export const redemptionInvestigationAPI = {
    list: (params) => api.get('/audit/redemption-investigation', { params }),
    stats: (params) => api.get('/audit/redemption-investigation/stats', { params }),
    exportData: (params) => api.get('/audit/redemption-investigation/export', {
        params,
        responseType: 'blob',
        timeout: 120000, // larger exports may legitimately take longer
    }),
};

// Export Permissions API
export const exportAPI = {
    getPermissions: () => api.get('/export/permissions'),
    validateExport: (exportType, resourceType, resourceId = null) => 
        api.post('/export/validate', { export_type: exportType, resource_type: resourceType, resource_id: resourceId }),
};

// Auth API extensions
export const changePassword = (currentPassword, newPassword) => 
    api.post('/auth/change-password', { current_password: currentPassword, new_password: newPassword });

// Seed API (for initial setup)
export const seedAPI = {
    seed: () => api.post('/seed'),
};

// v4.35.0 — Daily Portfolio Review (Welcome / Mission Control)
const browserTz = () => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Toronto'; }
    catch { return 'America/Toronto'; }
};

export const welcomeAPI = {
    check: () => api.get('/welcome/check', { params: { tz: browserTz() } }),
    dailyReview: () => api.get('/welcome/daily-review', { params: { tz: browserTz() } }),
    dismiss: () => api.post('/welcome/dismiss', null, { params: { tz: browserTz() } }),
    // P_USER_WELCOME_DISPATCH_2.0 — engagement endpoints.
    markSeen: (dispatchId) => api.patch(`/welcome/seen/${dispatchId}`),
    acknowledge: (dispatchId) => api.patch(`/welcome/acknowledge/${dispatchId}`),
};

// v4.35.0 — User preferences
export const preferencesAPI = {
    update: (payload) => api.put('/me/preferences', payload),
};

// P_WEEKLY_REPORT_CARD_1.0 — Executive ritual engine.
export const weeklyReportAPI = {
    generateNow: (organization_id, week_end = null) =>
        api.post('/weekly-report/generate-now', { organization_id, week_end }),
    sendNow: (organization_id, snapshot_id = null) =>
        api.post('/weekly-report/send-now', { organization_id, snapshot_id }),
    sendTest: (organization_id, recipient, snapshot_id = null) =>
        api.post('/weekly-report/send-test', { organization_id, recipient, snapshot_id }),
    resend: (organization_id, snapshot_id) =>
        api.post('/weekly-report/resend', { organization_id, snapshot_id }),
    current: (organization_id, mode = 'snapshot', snapshot_id = null) => {
        const params = { organization_id, mode };
        if (snapshot_id) params.snapshot_id = snapshot_id;
        return api.get('/weekly-report/current', { params });
    },
    history: (organization_id, limit = 12) =>
        api.get('/weekly-report/history', { params: { organization_id, limit } }),
    previewEmailUrl: (organization_id, { mode = 'snapshot', snapshot_id = null } = {}) => {
        const params = new URLSearchParams({ organization_id, mode });
        if (snapshot_id) params.set('snapshot_id', snapshot_id);
        return `/weekly-report/preview-email?${params.toString()}`;
    },
};

// P_WEEKLY_REPORT_CARD_2.3 — Distribution Center.
export const weeklyDistributionAPI = {
    listRecipients: (organization_id) =>
        api.get('/weekly-report/distribution/recipients', { params: { organization_id } }),
    createRecipient: (payload) =>
        api.post('/weekly-report/distribution/recipients', payload),
    updateRecipient: (id, payload) =>
        api.patch(`/weekly-report/distribution/recipients/${id}`, payload),
    deleteRecipient: (id) =>
        api.delete(`/weekly-report/distribution/recipients/${id}`),

    listGroups: () => api.get('/weekly-report/distribution/groups'),
    createGroup: (payload) => api.post('/weekly-report/distribution/groups', payload),
    updateGroup: (id, payload) => api.patch(`/weekly-report/distribution/groups/${id}`, payload),
    deleteGroup: (id) => api.delete(`/weekly-report/distribution/groups/${id}`),

    getSettings: (organization_id) =>
        api.get('/weekly-report/distribution/settings', { params: { organization_id } }),
    updateSettings: (organization_id, payload) =>
        api.patch('/weekly-report/distribution/settings', payload, { params: { organization_id } }),
};

// P_WEEKLY_REPORT_CARD_3.0 — Recognition layer (1st Star + Comment).
export const weeklyRecognitionAPI = {
    get: (organization_id, week_end) =>
        api.get('/weekly-report/recognition', { params: { organization_id, week_end } }),
    upsert: (payload) => api.patch('/weekly-report/recognition', payload),
};

// P_UNIFIED_VALUE_ENGINE_1.0 Phase 5 — observability surfaces. All
// strictly read-only; never mutate value math.
export const diagnosticsAPI = {
    valueEngineStatus: () =>
        api.get('/admin/diagnostics/value-engine-status'),
    orphanTaps: () => api.get('/admin/diagnostics/orphan-taps'),
    snapshotAudit: (organization_id = null) => {
        const params = {};
        if (organization_id) params.organization_id = organization_id;
        return api.get('/admin/diagnostics/snapshot-audit', { params });
    },
    reconciliation: (organization_id, start, end) =>
        api.get('/admin/diagnostics/reconciliation', {
            params: { organization_id, start, end },
        }),
};

// P_TAP_HEALTH_MONITOR_1.0 — Super-admin Tap Analytics Health Monitor.
export const tapHealthAPI = {
    summary: () => api.get('/admin/tap-health/summary'),
    throughput: () => api.get('/admin/tap-health/throughput'),
    suppressions: (hours = 24) => api.get('/admin/tap-health/suppressions', { params: { hours } }),
    directVsLanding: (hours = 24) => api.get('/admin/tap-health/direct-vs-landing', { params: { hours } }),
    silentCards: (days = 7, limit = 50) => api.get('/admin/tap-health/silent-cards', { params: { days, limit } }),
    writeFailures: (hours = 24, limit = 50) => api.get('/admin/tap-health/write-failures', { params: { hours, limit } }),
    alerts: () => api.get('/admin/tap-health/alerts'),
    trend: (days = 30) => api.get('/admin/tap-health/trend', { params: { days } }),
    cardDiagnostics: (card_id) => api.get(`/admin/tap-health/card-diagnostics/${encodeURIComponent(card_id)}`),
    cardEvents: (card_id, limit = 100) => api.get(`/admin/tap-health/card-events/${encodeURIComponent(card_id)}`, { params: { limit } }),
};

// P_USER_WELCOME_DISPATCH_3.0a — Campaign Authoring.
export const campaignsAPI = {
    list: (params = {}) => api.get('/campaigns', { params }),
    get: (id) => api.get(`/campaigns/${id}`),
    create: (payload) => api.post('/campaigns', payload),
    // P_USER_WELCOME_DISPATCH_3.0b — draft edit + schedule.
    update: (id, payload) => api.patch(`/campaigns/${id}`, payload),
    schedule: (id, scheduled_for) => api.post(`/campaigns/${id}/schedule`, { scheduled_for }),
    audiencePreview: (target_scope) =>
        api.post('/campaigns/audience-preview', { target_scope }),
    sendNow: (id) => api.post(`/campaigns/${id}/send-now`),
    cancel: (id) => api.post(`/campaigns/${id}/cancel`),
    listDispatches: (id, params = {}) =>
        api.get(`/campaigns/${id}/dispatches`, { params }),
    getDispatch: (campaignId, dispatchId) =>
        api.get(`/campaigns/${campaignId}/dispatches/${dispatchId}`),
    markSeen: (dispatchId) => api.patch(`/campaigns/seen/${dispatchId}`),
    acknowledge: (dispatchId) => api.patch(`/campaigns/acknowledge/${dispatchId}`),
};

// P_COMMUNICATION_ANALYTICS_4.0a — Read-only analytics endpoints.
export const analyticsAPI = {
    summary: (params = {}) => api.get('/communications/analytics/summary', { params }),
    welcome: (params = {}) => api.get('/communications/analytics/welcome', { params }),
    campaigns: (params = {}) => api.get('/communications/analytics/campaigns', { params }),
};

// P_PLATFORM_TIMEZONE_ET_1.0 + P_WELCOME_HUB_SCHEDULER_TAB_1.0 —
// Super Admin scheduler control. Health is a structured snapshot
// (see services/welcome_scheduler.py health_snapshot()). enable/disable
// persist the override in Mongo so state survives backend restarts;
// run-now respects the per-org due window; force-dispatch bypasses it.
export const welcomeSchedulerAPI = {
    health: () => api.get('/welcome/scheduler/health'),
    enable: () => api.post('/welcome/scheduler/enable'),
    disable: () => api.post('/welcome/scheduler/disable'),
    runNow: () => api.post('/welcome/scheduler/run-now'),
    forceDispatch: () => api.post('/welcome/scheduler/force-dispatch'),
    recentRuns: (limit = 10) => api.get('/welcome/scheduler/recent-runs', { params: { limit } }),
};

export default api;
